/**
 * Shared Supabase environment variable helpers.
 *
 * Centralises the reading and validation of the two public Supabase env vars
 * (`NEXT_PUBLIC_SUPABASE_URL` and `NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY`) so
 * that `client.ts`, `server.ts`, and `middleware.ts` don't each duplicate the
 * same logic.
 */

export interface SupabaseEnv {
  url: string;
  anonKey: string;
}

/** Build an error message listing whichever env vars are absent. */
function missingVarsMessage(url: string | undefined, key: string | undefined): string {
  const missing: string[] = [];
  if (!url) missing.push("NEXT_PUBLIC_SUPABASE_URL");
  if (!key) missing.push("NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY");
  return `Missing Supabase environment variable(s): ${missing.join(", ")}. Set them in .env.local or your deployment environment.`;
}

/**
 * Return the public Supabase env vars or throw with a descriptive message.
 *
 * Use in `client.ts` and `server.ts` where a missing config is a hard error.
 */
export function getSupabaseEnv(): SupabaseEnv {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const key = process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY;

  if (!url || !key) {
    throw new Error(missingVarsMessage(url, key));
  }

  return { url, anonKey: key };
}

/**
 * Return the public Supabase env vars, or `null` if either is missing.
 *
 * Use in middleware where a missing config should degrade gracefully (pass the
 * request through) rather than crash.
 */
export function getSupabaseEnvOrNull(): SupabaseEnv | null {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const key = process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY;

  if (!url || !key) {
    return null;
  }

  return { url, anonKey: key };
}
